import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const newId = () => `s_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
const withIds = (msgs) => msgs.map((m, i) => ({ ...m, id: m.id || `${m.created_at || "h"}-${m.role}-${i}` }));

async function readSse(res, onEvent) {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.filter((p) => p.startsWith("data: ")).forEach((p) => onEvent(JSON.parse(p.slice(6))));
  }
}

function useChatHistory(enabled, sessionId) {
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const loadSessions = useCallback(() => {
    if (enabled) api.get("/ai/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  }, [enabled]);
  useEffect(() => {
    if (!enabled) { setMessages([]); return; }
    api.get(`/ai/chat/${sessionId}`).then(({ data }) => setMessages(withIds(data))).catch(() => setMessages([]));
    loadSessions();
  }, [enabled, sessionId, loadSessions]);
  return { messages, setMessages, sessions, loadSessions };
}

export function useAiChat(enabled) {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ai_session") || newId());
  const { messages, setMessages, sessions, loadSessions } = useChatHistory(enabled, sessionId);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => { localStorage.setItem("ai_session", sessionId); }, [sessionId]);

  const newChat = () => { setSessionId(newId()); setMessages([]); setError(null); };
  const switchTo = (id) => { setSessionId(id); setError(null); };

  const appendDelta = (delta) => setMessages((m) => {
    const c = [...m];
    const last = { ...c[c.length - 1], pending: false };
    last.content += delta;
    c[c.length - 1] = last;
    return c;
  });

  const send = async (text) => {
    const msg = text.trim();
    if (!msg || streaming) return;
    setError(null);
    const stamp = Date.now();
    setMessages((m) => [...m, { id: `u-${stamp}`, role: "user", content: msg }, { id: `a-${stamp}`, role: "assistant", content: "", pending: true }]);
    setStreaming(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API}/ai/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId, message: msg }), signal: ctrl.signal });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Request failed"); }
      await readSse(res, (ev) => {
        if (ev.delta) appendDelta(ev.delta);
        if (ev.error) throw new Error(ev.error);
      });
      loadSessions();
    } catch (e) {
      if (e.name !== "AbortError") { setError(e.message); setMessages((m) => m.filter((x) => !(x.pending && !x.content))); }
    } finally { setStreaming(false); abortRef.current = null; }
  };

  const stop = () => abortRef.current?.abort();
  return { sessionId, messages, sessions, streaming, error, send, stop, newChat, switchTo };
}
